import { changeEmailApiV1AuthChangeEmailPost, updateMyProfileApiV1UsersMePut } from '@/common/api/client/sdk.gen'
import type {
    ChangeEmailApiV1AuthChangeEmailPostError,
    UpdateMyProfileApiV1UsersMePutError,
    UserResponse,
    UserUpdate,
} from '@/common/api/client/types.gen'
import { hasUserEmail } from '@/common/entities/me/lib/profileCompleteness'
import { useMe } from '@/common/entities/me/model/me'
import { useFormMutation } from '@/common/hooks/useFormMutation'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { AxiosError } from 'axios'

export type MeFormData = UserUpdate & {
    email?: string
}

type MeFormError = UpdateMyProfileApiV1UsersMePutError | ChangeEmailApiV1AuthChangeEmailPostError

export const useUpdateMe = () => {
    const { me, isLoading: meLoading } = useMe()
    const queryClient = useQueryClient()
    const canEditEmail = !hasUserEmail(me)

    const { form, onSubmit, register, handleSubmit, isLoading, isError, error } = useFormMutation<
        MeFormData,
        UserResponse,
        MeFormError
    >({
        mutationFn: async data => {
            const { email, ...profileData } = data
            const updatedProfile = await updateMyProfileApiV1UsersMePut({ body: profileData }).then(res => res.data!)

            if (canEditEmail && email?.trim()) {
                const emailResult = await changeEmailApiV1AuthChangeEmailPost({
                    body: { email: email.trim() },
                }).then(res => res.data!)

                return emailResult
            }

            return updatedProfile
        },
        formOptions: {
            defaultValues: {
                first_name: '',
                last_name: '',
                phone: '',
                timezone: '',
                locale: '',
                email: '',
            },
        },
        toastOptions: {
            successMessage: (_data, formData) => {
                if (canEditEmail && formData.email?.trim()) {
                    return `Письмо с подтверждением отправлено на ${formData.email.trim()}`
                }

                return 'Профиль обновлен'
            },
        },
        onSuccess: updatedProfile => {
            queryClient.setQueryData(['me'], updatedProfile)
        },
        onError: mutationError => {
            const message =
                (mutationError.response?.data.detail as unknown as string) || 'Ошибка при обновлении профиля'
            form.setError('root', { type: 'server', message })
        },
    })

    useEffect(() => {
        if (me) {
            form.reset({
                first_name: me.first_name ?? '',
                last_name: me.last_name ?? '',
                phone: me.phone ?? '',
                timezone: me.timezone ?? '',
                locale: me.locale ?? '',
                email: canEditEmail ? '' : undefined,
            })
        }
    }, [canEditEmail, form, me])

    return {
        form,
        me,
        meLoading,
        canEditEmail,
        handleSubmit,
        onSubmit,
        register,
        isPending: isLoading,
        isError,
        error: error as AxiosError<MeFormError> | null,
    }
}
